import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { candidatePortalAPI } from '../services/api';
import './ApplicationForm.css';

const ApplicationForm = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentCompany: '',
    currentPosition: '',
    totalExperience: '',
    noticePeriod: '',
    expectedSalary: '',
    coverLetter: '',
    source: 'direct_application'
  });
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const loadJobDetails = async () => {
      try {
        const response = await candidatePortalAPI.getJobDetails(jobId);
        // handle both { success, data } and direct object responses
        if (response.data?.success) {
          setJob(response.data.data);
        } else if (response.data?._id) {
          setJob(response.data);
        }
      } catch (error) {
        console.error('Error loading job details:', error);
      }
    };

    if (jobId) {
      loadJobDetails();
    }
  }, [jobId]);

  const steps = [
    { number: 1, title: 'Personal Info' },
    { number: 2, title: 'Professional Info' },
    { number: 3, title: 'Documents' }
  ];

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    }
    
    if (step === 3 && !resume) {
      newErrors.resume = 'Resume is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) return;

    setLoading(true);
    setErrors({});
    
    try {
      const submitData = new FormData();
      
      // Append all form data
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });
      
      // Append job ID
      submitData.append('jobId', jobId);
      
      // Append resume
      if (resume) {
        submitData.append('resume', resume);
      }

      const response = await candidatePortalAPI.submitApplication(submitData);
      
      if (response.data.success) {
        navigate('/application-success', { 
          state: { 
            candidate: response.data.data,
            job: job
          }
        });
      } else {
        setErrors({ submit: response.data.message || 'Application failed' });
      }
      
    } catch (error) {
      console.error('Application error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Error submitting application. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 
                           'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024;
      
      if (!allowedTypes.includes(file.type)) {
        setErrors({...errors, resume: 'Please upload PDF or Word document'});
        return;
      }
      
      if (file.size > maxSize) {
        setErrors({...errors, resume: 'File size must be less than 5MB'});
        return;
      }
      
      setResume(file);
      setErrors({...errors, resume: ''});
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="application-form-container">
      {/* Header with Job Info */}
      <div className="application-header">
        <button onClick={handleCancel} className="back-button">
          ← Back
        </button>
        <div className="job-info">
          <h2>Apply for {job?.jobTitle}</h2>
          <p>{job?.department} • {job?.location}</p>
        </div>
      </div>

      <div className="form-progress">
        {steps.map(step => (
          <div key={step.number} className={`progress-step ${currentStep >= step.number ? 'active' : ''}`}>
            <div className="step-number">{step.number}</div>
            <span className="step-title">{step.title}</span>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="application-form">
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div className="form-step">
            <h3>Personal Information</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                  placeholder="Enter your phone number"
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleNext}
              >
                Next: Professional Info
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Professional Information */}
        {currentStep === 2 && (
          <div className="form-step">
            <h3>Professional Information</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Current Company</label>
                <input
                  type="text"
                  name="currentCompany"
                  value={formData.currentCompany}
                  onChange={handleChange}
                  placeholder="Your current company"
                />
              </div>
              
              <div className="form-group">
                <label>Current Position</label>
                <input
                  type="text"
                  name="currentPosition"
                  value={formData.currentPosition}
                  onChange={handleChange}
                  placeholder="Your current position"
                />
              </div>

              <div className="form-group">
                <label>Total Experience (Years)</label>
                <input
                  type="number"
                  name="totalExperience"
                  value={formData.totalExperience}
                  onChange={handleChange}
                  placeholder="e.g., 5"
                  min="0"
                  step="0.5"
                />
              </div>

              <div className="form-group">
                <label>Notice Period (Days)</label>
                <input
                  type="number"
                  name="noticePeriod"
                  value={formData.noticePeriod}
                  onChange={handleChange}
                  placeholder="e.g., 30"
                  min="0"
                />
              </div>

              <div className="form-group full-width">
                <label>Expected Salary (₹)</label>
                <input
                  type="number"
                  name="expectedSalary"
                  value={formData.expectedSalary}
                  onChange={handleChange}
                  placeholder="Expected annual salary"
                  min="0"
                />
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={handleBack}
              >
                ← Back
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleNext}
              >
                Next: Documents
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Documents */}
        {currentStep === 3 && (
          <div className="form-step">
            <h3>Documents & Final Details</h3>
            
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Upload Resume *</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div 
                    className={`upload-box ${resume ? 'has-file' : ''} ${errors.resume ? 'error' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {resume ? (
                      <div className="file-info">
                        <span className="file-icon">📄</span>
                        <div className="file-details">
                          <div className="file-name">{resume.name}</div>
                          <div className="file-size">
                            {(resume.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                        <button 
                          type="button" 
                          className="remove-file"
                          onClick={(e) => {
                            e.stopPropagation();
                            setResume(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon">📎</div>
                        <div className="upload-text">
                          <div className="upload-title">Click to upload resume</div>
                          <div className="upload-subtitle">
                            Supports: PDF, DOC, DOCX | Max: 5MB
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.resume && <span className="error-text">{errors.resume}</span>}
                </div>
              </div>

              <div className="form-group full-width">
                <label>Cover Letter</label>
                <textarea
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleChange}
                  placeholder="Tell us why you're interested in this position and what makes you a great candidate..."
                  rows="5"
                />
              </div>
            </div>

            {errors.submit && (
              <div className="error-message">
                {errors.submit}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={handleBack}
              >
                ← Back
              </button>
              <button 
                type="submit" 
                className={`btn btn-primary ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ApplicationForm;